import { useState } from "react";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ScheduleOutlinedIcon from "@mui/icons-material/ScheduleOutlined";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useTranslate } from "@refinedev/core";
import { useForm } from "react-hook-form";

import { Form } from "@shared/components";
import { AgreementsAutocomplete } from "@shared/components/AutocompleteFields";
import { getAgreementsByIdService } from "@modules/agreements/services/queries/listAgreements/service";
import { CategoryMedal } from "@modules/financial/components/CategoryMedal";
import { formatFinancialError } from "@modules/financial/nfe";
import {
  financialQueryKey,
  getVolumetryEmailService,
  listVolumetrySentService,
} from "@modules/financial/services";
import { VolumetryEmail, VolumetrySentEmail, VolumetrySentFile } from "@modules/financial/types";

const MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];
const TABLE_TAG = "{{volumetria}}";
const SIGNATURE_TAG = "{{assinatura}}";
const SIGNATURE_SRC = "/financial/assinatura-financeiro.jpg";

interface SentFilterForm {
  agreement_id: number | null;
  year: string;
  month: string;
  mode: "" | "manual" | "automatic";
  email: string;
  q: string;
}

function yearOptions() {
  const current = new Date().getFullYear();
  return Array.from({ length: 6 }, (_, index) => current - 4 + index);
}

function periodLabel(month: number, year: number) {
  return `${String(month).padStart(2, "0")}/${year}`;
}

function shortFileName(filename: string, max = 32) {
  if (filename.length <= max) return filename;
  const dot = filename.lastIndexOf(".");
  const ext = dot > 0 ? filename.slice(dot) : "";
  const base = dot > 0 ? filename.slice(0, dot) : filename;
  const room = max - ext.length - 1;
  if (room < 8) return `${filename.slice(0, max - 1)}…`;
  return `${base.slice(0, room)}…${ext}`;
}

function escapeRegExp(value: string) {
  return value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function splitBody(body: string, tags: string[]) {
  const unique = [...new Set(tags.filter(Boolean))];
  if (unique.length === 0) return [body];
  return body.split(new RegExp(`(${unique.map(escapeRegExp).join("|")})`, "g"));
}

function automaticFilter(mode: SentFilterForm["mode"]) {
  if (mode === "automatic") return true;
  if (mode === "manual") return false;
  return undefined;
}

function rowTone(row: { fill?: string; font?: string }) {
  if (!row.fill) return undefined;
  return { bgcolor: `#${row.fill}`, color: `#${row.font ?? "000000"}`, fontWeight: 700 };
}

function resolveSentFiles(email: VolumetrySentEmail, draft?: VolumetryEmail): VolumetrySentEmail {
  if (!draft) return email;
  const current = [...draft.images, ...draft.attachments];
  return {
    ...email,
    files: email.files.map((file) => {
      if (file.url) return file;
      const match = current.find(
        (item) => item.id === file.id && item.filename === file.filename && item.url
      );
      return match?.url ? { ...file, url: match.url } : file;
    }),
  };
}

export function VolumetrySentPanel() {
  const translate = useTranslate();
  const methods = useForm<SentFilterForm>({
    defaultValues: { agreement_id: null, year: "", month: "", mode: "", email: "", q: "" },
  });
  const [applied, setApplied] = useState<SentFilterForm>(methods.getValues());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [selected, setSelected] = useState<VolumetrySentEmail | null>(null);

  const email = applied.email.trim();
  const text = applied.q.trim();
  const query = useQuery({
    queryKey: [financialQueryKey, "volumetry_email_sent", applied, page, pageSize],
    queryFn: () =>
      listVolumetrySentService({
        agreement_id: applied.agreement_id ?? undefined,
        year: applied.year ? Number(applied.year) : undefined,
        month: applied.month ? Number(applied.month) : undefined,
        email: email || undefined,
        q: text || undefined,
        automatic: automaticFilter(applied.mode),
        page: page + 1,
        page_size: pageSize,
      }),
  });

  const rows = query.data?.results ?? [];
  const count = query.data?.count ?? 0;
  const agreementIds = [...new Set(rows.map((row) => row.agreement_id))];
  const agreementQueries = useQueries({
    queries: agreementIds.map((id) => ({
      queryKey: ["agreements", "by-id", id],
      queryFn: () => getAgreementsByIdService(String(id)),
      staleTime: 5 * 60 * 1000,
    })),
  });
  const agreementNames = new Map<number, string>();
  agreementQueries.forEach((item, index) => {
    const id = agreementIds[index];
    if (id !== undefined && item.data?.name) agreementNames.set(id, item.data.name);
  });
  const agreementLabel = (row: Pick<VolumetrySentEmail, "agreement_id" | "agreement_name">) => {
    const name = agreementNames.get(row.agreement_id);
    if (name) return name;
    const request = agreementQueries[agreementIds.indexOf(row.agreement_id)];
    return request && !request.isFetched ? "" : row.agreement_name;
  };
  const draftIds = [
    ...new Set(
      rows
        .filter((row) => row.files.some((file) => file.kind === "attachment" && !file.url))
        .map((row) => row.volumetry_email_id)
    ),
  ];
  const draftQueries = useQueries({
    queries: draftIds.map((id) => ({
      queryKey: [financialQueryKey, "volumetry_email", id],
      queryFn: () => getVolumetryEmailService(id),
      staleTime: 60 * 1000,
    })),
  });
  const drafts = new Map<number, VolumetryEmail>();
  draftQueries.forEach((item, index) => {
    const id = draftIds[index];
    if (id !== undefined && item.data) drafts.set(id, item.data);
  });
  const draftQuery = useQuery({
    queryKey: [financialQueryKey, "volumetry_email", selected?.volumetry_email_id],
    queryFn: () => getVolumetryEmailService(selected!.volumetry_email_id),
    enabled: selected !== null && selected.files.some((file) => !file.url),
  });
  const detail = selected
    ? resolveSentFiles(selected, drafts.get(selected.volumetry_email_id) ?? draftQuery.data)
    : null;
  const attachmentsOf = (row: VolumetrySentEmail) =>
    resolveSentFiles(row, drafts.get(row.volumetry_email_id)).files.filter(
      (file) => file.kind === "attachment"
    );
  const errorMessage = query.isError
    ? formatFinancialError(query.error, translate("financial_admin.volumetry_sent.load_failed"))
    : "";

  const search = (values: SentFilterForm) => {
    setApplied(values);
    setPage(0);
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "grey.50" }}>
        <Form methods={methods} onSubmit={search}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, alignItems: "flex-end" }}>
            <Box sx={{ width: { xs: "100%", sm: 320 }, "& .MuiAutocomplete-root": { width: "100%" } }}>
              <AgreementsAutocomplete
                name="agreement_id"
                label={translate("financial_admin.filters.agreement")}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 140 } }}>
              <Form.Select
                name="year"
                label={translate("financial_admin.filters.year")}
                options={[
                  { value: "", label: translate("financial_admin.filters.all") },
                  ...yearOptions().map((year) => ({ value: String(year), label: String(year) })),
                ]}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 180 } }}>
              <Form.Select
                name="month"
                label={translate("financial_admin.filters.month")}
                options={[
                  { value: "", label: translate("financial_admin.filters.all") },
                  ...MONTHS.map((month) => ({
                    value: String(month),
                    label: translate(`financial_admin.months.${month}`),
                  })),
                ]}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 180 } }}>
              <Form.Select
                name="mode"
                label={translate("financial_admin.volumetry_sent.mode")}
                options={[
                  { value: "", label: translate("financial_admin.filters.all") },
                  { value: "manual", label: translate("financial_admin.volumetry_sent.manual") },
                  { value: "automatic", label: translate("financial_admin.volumetry.automatic") },
                ]}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 240 } }}>
              <Form.Input
                name="email"
                label={translate("financial_admin.volumetry_sent.email")}
                placeholder={translate("financial_admin.volumetry_sent.email_hint")}
              />
            </Box>
            <Box sx={{ width: { xs: "100%", sm: 240 } }}>
              <Form.Input
                name="q"
                label={translate("financial_admin.volumetry_sent.text")}
                placeholder={translate("financial_admin.volumetry_sent.text_hint")}
              />
            </Box>
            <Button type="submit" variant="contained" sx={{ height: 45, minWidth: 140 }}>
              {translate("financial_admin.actions.apply")}
            </Button>
          </Box>
        </Form>
      </Paper>

      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
        {query.isFetching && <LinearProgress />}
        {!query.isFetching && !query.isError && count === 0 ? (
          <Typography sx={{ p: 3 }} color="text.secondary">
            {translate("financial_admin.volumetry_sent.empty")}
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{translate("financial_admin.volumetry_sent.sent_at")}</TableCell>
                  <TableCell>{translate("financial_admin.fields.agreement")}</TableCell>
                  <TableCell>{translate("financial_admin.volumetry_sent.period")}</TableCell>
                  <TableCell>{translate("financial_admin.volumetry.to")}</TableCell>
                  <TableCell>{translate("financial_admin.volumetry.cc")}</TableCell>
                  <TableCell>{translate("financial_admin.volumetry.subject")}</TableCell>
                  <TableCell>{translate("financial_admin.volumetry_sent.files")}</TableCell>
                  <TableCell>{translate("financial_admin.volumetry_sent.mode")}</TableCell>
                  <TableCell>{translate("financial_admin.volumetry_sent.actor")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => setSelected(row)}
                    sx={{ cursor: "pointer" }}
                  >
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {new Date(row.sent_at).toLocaleString()}
                    </TableCell>
                    <TableCell>{agreementLabel(row)}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>{periodLabel(row.month, row.year)}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>{row.to_emails}</TableCell>
                    <TableCell sx={{ maxWidth: 220 }}>{row.cc_emails}</TableCell>
                    <TableCell sx={{ maxWidth: 280 }}>{row.subject}</TableCell>
                    <TableCell
                      sx={{ width: 240, maxWidth: 240, verticalAlign: "middle" }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <SentFileLinks files={attachmentsOf(row)} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <SendModeChip automatic={row.automatic} />
                    </TableCell>
                    <TableCell>{row.actor_name ?? ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}
        <TablePagination
          component="div"
          count={count}
          page={page}
          rowsPerPage={pageSize}
          rowsPerPageOptions={[25, 50, 100]}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          onRowsPerPageChange={(event) => {
            setPageSize(Number(event.target.value));
            setPage(0);
          }}
          labelRowsPerPage={translate("financial_admin.volumetry_sent.rows_per_page")}
        />
      </Paper>

      <Dialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        fullWidth
        maxWidth="md"
        slotProps={{ paper: { sx: { borderRadius: 3 } } }}
      >
        {detail && (
          <>
            <DialogTitle sx={{ pb: 0.5 }}>{detail.subject}</DialogTitle>
            <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {new Date(detail.sent_at).toLocaleString()}
                  {" · "}
                  {agreementLabel(detail)}
                  {" · "}
                  {periodLabel(detail.month, detail.year)}
                  {detail.actor_name ? ` · ${detail.actor_name}` : ""}
                </Typography>
                <SendModeChip automatic={detail.automatic} />
              </Box>
              <DetailBlock label={translate("financial_admin.volumetry.to")} value={detail.to_emails} />
              <DetailBlock label={translate("financial_admin.volumetry.cc")} value={detail.cc_emails} />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  {translate("financial_admin.volumetry.body")}
                </Typography>
                <SentBodyPreview email={detail} />
              </Box>
              <SentAttachments
                files={detail.files}
                title={translate("financial_admin.volumetry_sent.files")}
                emptyLabel={translate("financial_admin.volumetry_sent.files_empty")}
              />
              {detail.rows.length > 0 && (
                <Box sx={{ maxWidth: 420, border: 1, borderColor: "divider", borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>{translate("financial_admin.volumetry.plan")}</TableCell>
                        <TableCell align="right">{translate("financial_admin.volumetry.total")}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {detail.rows.map((row) => (
                        <TableRow key={row.key}>
                          <TableCell sx={{ py: 0.75, ...rowTone(row) }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                              <CategoryMedal name={row.plan} size={32} />
                              {row.plan}
                            </Box>
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.75, ...rowTone(row) }}>
                            {row.total}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSelected(null)}>{translate("financial_admin.actions.cancel")}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
}

function DetailBlock({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ mt: 0.5 }}>
        {value || "—"}
      </Typography>
    </Box>
  );
}

function SentBodyPreview({ email }: { email: VolumetrySentEmail }) {
  const parts = splitBody(email.body || "", [TABLE_TAG, SIGNATURE_TAG]);
  const tableFile = email.files.find((file) => file.kind === "image" && file.role === "table" && file.url);
  const extraImages = email.files.filter(
    (file) => file.kind === "image" && file.role !== "table" && file.url
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: "#fff", color: "#222" }}>
      <Box sx={{ fontSize: 14, lineHeight: 1.6 }}>
        {parts.map((part, index) => {
          if (part === TABLE_TAG) {
            if (!tableFile?.url) return null;
            return <PreviewImage key={`table-${index}`} src={tableFile.url} alt={tableFile.filename} />;
          }
          if (part === SIGNATURE_TAG) {
            return <PreviewImage key={`signature-${index}`} src={SIGNATURE_SRC} alt="" maxWidth={280} />;
          }
          if (!part) return null;
          return (
            <Typography
              key={`text-${index}`}
              component="div"
              variant="body2"
              sx={{ whiteSpace: "pre-wrap", color: "inherit" }}
            >
              {part}
            </Typography>
          );
        })}
        {extraImages.map((file) => (
          <PreviewImage key={file.id} src={file.url as string} alt={file.filename} />
        ))}
      </Box>
    </Paper>
  );
}

function PreviewImage({ src, alt, maxWidth = 400 }: { src: string; alt: string; maxWidth?: number }) {
  return (
    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{ display: "block", maxWidth, height: "auto", my: 1.5 }}
    />
  );
}

function SendModeChip({ automatic }: { automatic: boolean }) {
  const translate = useTranslate();

  return (
    <Chip
      size="small"
      variant="outlined"
      color={automatic ? "info" : "primary"}
      icon={automatic ? <ScheduleOutlinedIcon /> : <PersonOutlineIcon />}
      label={translate(
        automatic ? "financial_admin.volumetry.automatic" : "financial_admin.volumetry_sent.manual"
      )}
      sx={{ fontWeight: 600, "& .MuiChip-icon": { fontSize: 16 } }}
    />
  );
}

function SentFileLinks({ files }: { files: VolumetrySentFile[] }) {
  if (files.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "stretch", gap: 0.75, width: 220 }}>
      {files.map((file) => (
        <Tooltip key={file.id} title={file.filename}>
          <Box component="span" sx={{ display: "block", minWidth: 0 }}>
            <Button
              component={file.url ? "a" : "button"}
              href={file.url || undefined}
              download={file.url ? file.filename : undefined}
              target={file.url ? "_blank" : undefined}
              rel={file.url ? "noopener noreferrer" : undefined}
              disabled={!file.url}
              size="small"
              variant="outlined"
              startIcon={<FileDownloadOutlinedIcon />}
              onClick={(event) => event.stopPropagation()}
              sx={{
                width: "100%",
                textTransform: "none",
                justifyContent: "flex-start",
                minWidth: 0,
                "& .MuiButton-startIcon": { flexShrink: 0 },
              }}
            >
              <Box component="span" sx={{ overflow: "hidden", whiteSpace: "nowrap" }}>
                {shortFileName(file.filename, 22)}
              </Box>
            </Button>
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}

function SentAttachments({
  files,
  title,
  emptyLabel,
}: {
  files: VolumetrySentFile[];
  title: string;
  emptyLabel: string;
}) {
  const attachments = files.filter((file) => file.kind === "attachment");

  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {title}
      </Typography>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mt: 0.5 }}>
        {attachments.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            {emptyLabel}
          </Typography>
        ) : (
          attachments.map((file) =>
            file.url ? (
              <Link
                key={file.id}
                href={file.url}
                download={file.filename}
                target="_blank"
                rel="noopener noreferrer"
              >
                {file.filename}
              </Link>
            ) : (
              <Typography key={file.id} variant="body2">
                {file.filename}
              </Typography>
            )
          )
        )}
      </Box>
    </Box>
  );
}
